import { Save, Share2, Copy, Send, FileDown, CheckCircle, Eye, Palette, Lock } from 'lucide-react';
import PlanGate from '../../../../../../../components/gating/PlanGate';
import TravellerAppView from '../../../../../../DashBoard/sections/SampleVoucherSection/sections/TravellerAppView';
import { agentService } from '../../../../../../../services/agent/agentService';
import { toast } from 'react-hot-toast';

const ClientSharingSection = ({
  formData,
  bookingId,
  formVoucherTheme,
  isDownloading,
  isVoucherLocked,
  isSaved,
  showShareDropdown,
  setShowShareDropdown,
  generatedPdfUrl,
  executeSaveVoucher,
  generateWhatsAppMessage,
  clearDraft,
  navigate,
  setActiveTab,
  setIsPreviewModalOpen,
  isEditMode = false,
  isReviewMode = false,
  voucherId = null
}) => {
  return (
    <div className="bg-gradient-to-br from-[#067978]/5 via-[#067978]/10 to-teal-50/20 rounded-[2.5rem] border border-[#067978]/10 p-6 md:p-8 shadow-sm flex flex-col md:flex-row items-center gap-8 relative mt-8">
      <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-3xl pointer-events-none -z-10"></div>

      <div className="flex-1 space-y-4 text-left">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#f0f9f9] rounded-full text-[#067978] text-[10px] font-normal uppercase tracking-widest">
          ✨ Next-Gen Client Sharing
        </div>
        <h3 className="text-2xl md:text-3xl font-extrabold text-slate-800 leading-tight">
          Introduce Your Travellers to <span className="text-[#067978]">TRRIP App</span>
        </h3>
        <p className="text-gray-500 text-sm font-medium leading-relaxed max-w-xl">
          Say goodbye to scattered PDFs. Give your clients a fully white-labeled mobile assistant containing their hotel bookings, day-wise itineraries, driver details, and live recommendation maps.
        </p>

        <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 relative max-w-xl">
          {/* Button 1: Save Voucher / Create Voucher */}
          <PlanGate
            featureKey="voucher-creation"
            featureName="Voucher Creation"
            tooltipText="Subscription required to create vouchers."
            showLockIcon={false}
            wrapperClassName="w-full"
          >
            {({ isLocked }) => (
              <button
                type="button"
                onClick={() => {
                  if (isLocked || isVoucherLocked) {
                    navigate('/subscription');
                    toast.error('Active subscription required to create vouchers. Upgrade to unlock.');
                  } else {
                    executeSaveVoucher('only-save');
                  }
                }}
                disabled={isDownloading}
                className="w-full px-5 py-3.5 bg-white border border-[#067978]/20 text-[#067978] hover:bg-slate-50 rounded-2xl font-extrabold text-sm active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs"
              >
                {isDownloading ? <div className="w-4 h-4 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin"></div> : <Save size={18} />}
                <span>{isEditMode || isReviewMode ? 'Save Voucher' : 'Create Voucher'}</span>
                {(isLocked || isVoucherLocked) && <Lock size={14} className="ml-1 text-[#067978]/80 shrink-0" />}
              </button>
            )}
          </PlanGate>

          {/* Button 2: Share App */}
          <div className="relative w-full">
            <button
              type="button"
              onClick={() => {
                if (!isSaved) {
                  executeSaveVoucher('share');
                } else {
                  setShowShareDropdown(prev => !prev);
                }
              }}
              disabled={isDownloading}
              className="w-full px-5 py-3.5 bg-[#067978] hover:bg-[#056362] text-white rounded-2xl font-extrabold text-sm shadow-lg shadow-teal-900/10 active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {isDownloading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Share2 size={18} />}
              <span>Share App</span>
            </button>

            {showShareDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowShareDropdown(false)} />
                <div className="absolute left-0 sm:right-0 sm:left-auto mt-2 w-64 rounded-2xl bg-white border border-slate-200 shadow-2xl p-3 z-50 animate-in fade-in slide-in-from-top-2 duration-200 text-left space-y-1">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2.5 pb-1.5 border-b border-slate-100 mb-1">
                    Share Options
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('has_shared_app', 'true');
                      localStorage.setItem('trrip_step_share_completed', 'true');
                      agentService.markAppShared();
                      navigator.clipboard.writeText(generateWhatsAppMessage());
                      toast.success("Message copied! Redirecting to vouchers...");
                      setShowShareDropdown(false);
                      if (!isEditMode && !isReviewMode) clearDraft();
                      setTimeout(() => {
                        navigate('/voucher');
                        if (setActiveTab) setActiveTab('voucher');
                      }, 1000);
                    }}
                    className="w-full text-left px-3 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-xl transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <Copy size={16} className="text-slate-400" /> Copy Message Text
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('has_shared_app', 'true');
                      localStorage.setItem('trrip_step_share_completed', 'true');
                      agentService.markAppShared();
                      const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(generateWhatsAppMessage())}`;
                      window.open(whatsappUrl, '_blank');
                      toast.success("Shared via WhatsApp! Redirecting to vouchers...");
                      setShowShareDropdown(false);
                      if (!isEditMode && !isReviewMode) clearDraft();
                      setTimeout(() => {
                        navigate('/voucher');
                        if (setActiveTab) setActiveTab('voucher');
                      }, 1000);
                    }}
                    className="w-full text-left px-3 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-xl transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <Send size={16} className="text-slate-400" /> Share via WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem('has_shared_app', 'true');
                      localStorage.setItem('trrip_step_share_completed', 'true');
                      agentService.markAppShared();
                      setShowShareDropdown(false);

                      const guestName = formData?.guest_information?.guest_name || 'Guest';
                      const downloadFileName = `${guestName}_Voucher.pdf`;

                      if (generatedPdfUrl && generatedPdfUrl.startsWith('blob:')) {
                        const link = document.createElement('a');
                        link.href = generatedPdfUrl;
                        link.download = downloadFileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success("Downloading voucher! Redirecting to vouchers...");
                        if (!isEditMode && !isReviewMode) clearDraft();
                        setTimeout(() => {
                          navigate('/voucher');
                          if (setActiveTab) setActiveTab('voucher');
                        }, 1000);
                      } else if (typeof executeSaveVoucher === 'function') {
                        executeSaveVoucher('download');
                      }
                    }}
                    className="w-full text-left px-3 py-2.5 text-xs font-bold text-[#067978] hover:bg-teal-50 rounded-xl transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <FileDown size={16} className="text-[#067978]" /> Download Voucher PDF
                  </button>
                  <div className="pt-1 border-t border-slate-100 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setShowShareDropdown(false);
                        if (!isEditMode && !isReviewMode) clearDraft();
                        navigate('/voucher');
                        if (setActiveTab) setActiveTab('voucher');
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-bold text-[#067978] hover:bg-teal-50 rounded-xl transition-all flex items-center gap-2 cursor-pointer"
                    >
                      <CheckCircle size={15} className="text-[#067978]" /> Back to Vouchers
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Button 3: Preview PDF */}
          <button
            type="button"
            onClick={() => setIsPreviewModalOpen(true)}
            className="w-full px-5 py-3.5 bg-white border border-[#067978]/20 text-[#067978] hover:bg-slate-50 rounded-2xl font-extrabold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs active:scale-95"
          >
            <Eye size={18} />
            <span>Preview PDF</span>
          </button>

          {/* Button 4: Voucher Template */}
          <button
            type="button"
            onClick={() => navigate('/voucher-template', {
              state: {
                returnUrl: isEditMode && voucherId ? `/voucher-edit/${voucherId}` : '/create-voucher?tab=manual',
                activeTab: 'manual',
                currentTheme: formVoucherTheme,
                initialData: {
                  ...formData,
                  booking_id: bookingId,
                  bookingId: bookingId
                },
                bookingId: bookingId,
                isEditMode,
                isReviewMode,
                voucherId
              }
            })}
            className="w-full px-5 py-3.5 bg-white border border-[#067978]/20 text-[#067978] hover:bg-slate-50 rounded-2xl font-extrabold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs active:scale-95"
          >
            <Palette size={18} />
            <span>Voucher Template</span>
          </button>
        </div>
      </div>

      {/* Interactive Traveller App Preview */}
      <div className="w-full md:w-[260px] shrink-0 flex justify-center">
        <TravellerAppView />
      </div>
    </div>
  );
};

export default ClientSharingSection;
